import React from 'react';
import { CommandGroup, CommandItem } from '../ui/command';
import { Loader2, Briefcase } from 'lucide-react';
import { SearchResults as SearchResultsType } from '../../types/search';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import SearchSuggestions from './SearchSuggestions';
import { useSearchStore } from '@/store/searchStore';
import SearchResultItem from './SearchResultItem';

interface SearchResultsProps {
  isLoading: boolean;
  query: string;
  results?: SearchResultsType;
  hasSearched: boolean;
  onSelect: (type: string, id: string) => void;
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  isLoading,
  query,
  results = { players: [], posts: [], companies: [], clubs: [] },
  hasSearched,
  onSelect,
}) => {
  const { isDarkMode } = useTheme();
  const { handleSuggestionSelect } = useSearchStore();

  const hasAny =
    (results.players?.length ?? 0) +
    (results.posts?.length ?? 0) +
    (results.clubs?.length ?? 0) +
    (results.companies?.length ?? 0) > 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2
          className={cn(
            'h-6 w-6 animate-spin',
            isDarkMode ? 'text-dark-800' : 'text-gray-400'
          )}
        />
      </div>
    );
  }

  if (!query) {
    return <SearchSuggestions onSelect={handleSuggestionSelect} />;
  }

  if (hasSearched && !hasAny) {
    return (
      <div className={cn('py-6 text-center', isDarkMode ? 'text-dark-800' : 'text-gray-500')}>
        No results found for "{query}"
      </div>
    );
  }

  return (
    <>
      {results.players?.length > 0 && (
        <CommandGroup heading="Players">
          {results.players.map((player, idx) => (
            <SearchResultItem
              key={player.id ?? `player-${idx}`}
              item={{
                id: player.id,
                name: player.name,
                image: player.image,
                jobTitle: player.jobTitle,
                company: player.company,
                city: player.city,
                sports: player.sports,
                connectionCount: player.connectionCount,
              }}
              type={player.role === 'trainer' ? 'trainer' : 'player'}
              onSelect={onSelect}
            />
          ))}
        </CommandGroup>
      )}

      {results.posts?.length > 0 && (
        <CommandGroup heading="Posts">
          {results.posts.map((post, idx) => (
            <SearchResultItem
              key={post.id ?? `post-${idx}`}
              item={post}
              type="post"
              onSelect={onSelect}
            />
          ))}
        </CommandGroup>
      )}

      {results.clubs?.length > 0 && (
        <CommandGroup heading="Clubs">
          {results.clubs.map((club, idx) => (
            <SearchResultItem
              key={club.id ?? `club-${idx}`}
              item={club}
              type="club"
              onSelect={onSelect}
            />
          ))}
        </CommandGroup>
      )}

      {results.companies?.length > 0 && (
        <CommandGroup heading="Companies">
          {results.companies.map((company, idx) => (
            <CommandItem key={idx} onSelect={() => onSelect('company', company)}>
              <Briefcase className="mr-2 h-4 w-4 text-gray-500" />
              {company}
            </CommandItem>
          ))}
        </CommandGroup>
      )}
    </>
  );
};
